import { supabase } from '../lib/supabase';

/**
 * Fetch all templates for the current user
 */
export const fetchUserTemplates = async () => {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      ?.from('campaign_templates')
      ?.select('*')
      ?.eq('artist_id', user?.id)
      ?.order('created_at', { ascending: false });

    if (error) {
      throw new Error(error.message);
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching user templates:', error);
    throw error;
  }
};

/**
 * Fetch public templates (optionally filtered by genre)
 */
export const fetchPublicTemplates = async (genre = null) => {
  try {
    let query = supabase
      ?.from('campaign_templates')
      ?.select('*')
      ?.eq('is_public', true);

    if (genre) {
      query = query?.eq('genre', genre);
    }

    query = query?.order('use_count', { ascending: false });

    const { data, error } = await query;

    if (error) {
      throw new Error(error.message);
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching public templates:', error);
    throw error;
  }
};

/**
 * Fetch templates by genre (user's own + public)
 */
export const fetchTemplatesByGenre = async (genre) => {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      ?.from('campaign_templates')
      ?.select('*')
      ?.eq('genre', genre)
      ?.or(`artist_id.eq.${user?.id},is_public.eq.true`)
      ?.order('is_public', { ascending: false })
      ?.order('use_count', { ascending: false });

    if (error) {
      throw new Error(error.message);
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching templates by genre:', error);
    throw error;
  }
};

/**
 * Create a new campaign template
 */
export const createTemplate = async (templateData) => {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      ?.from('campaign_templates')
      ?.insert({
        artist_id: user?.id,
        template_name: templateData?.templateName,
        genre: templateData?.genre,
        description: templateData?.description,
        objectives: templateData?.objectives || [],
        budget: parseFloat(templateData?.budget) || 0,
        cost_per_engagement: templateData?.costPerEngagement ? parseFloat(templateData?.costPerEngagement) : null,
        payment_schedule: templateData?.paymentSchedule || 'milestone',
        content_types: templateData?.contentTypes || [],
        creative_guidelines: templateData?.creativeGuidelines,
        hashtags: templateData?.hashtags || [],
        min_followers: templateData?.minFollowers ? parseInt(templateData?.minFollowers) : null,
        max_followers: templateData?.maxFollowers ? parseInt(templateData?.maxFollowers) : null,
        min_engagement: templateData?.minEngagement ? parseFloat(templateData?.minEngagement) : null,
        content_specialization: templateData?.contentSpecialization || [],
        is_public: templateData?.isPublic || false
      })
      ?.select()
      ?.single();

    if (error) {
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    console.error('Error creating template:', error);
    throw error;
  }
};

/**
 * Update an existing template
 */
export const updateTemplate = async (templateId, templateData) => {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      ?.from('campaign_templates')
      ?.update({
        template_name: templateData?.templateName,
        genre: templateData?.genre,
        description: templateData?.description,
        objectives: templateData?.objectives || [],
        budget: parseFloat(templateData?.budget) || 0,
        cost_per_engagement: templateData?.costPerEngagement ? parseFloat(templateData?.costPerEngagement) : null,
        payment_schedule: templateData?.paymentSchedule || 'milestone',
        content_types: templateData?.contentTypes || [],
        creative_guidelines: templateData?.creativeGuidelines,
        hashtags: templateData?.hashtags || [],
        min_followers: templateData?.minFollowers ? parseInt(templateData?.minFollowers) : null,
        max_followers: templateData?.maxFollowers ? parseInt(templateData?.maxFollowers) : null,
        min_engagement: templateData?.minEngagement ? parseFloat(templateData?.minEngagement) : null,
        content_specialization: templateData?.contentSpecialization || [],
        is_public: templateData?.isPublic || false
      })
      ?.eq('id', templateId)
      ?.eq('artist_id', user?.id)
      ?.select()
      ?.single();

    if (error) {
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    console.error('Error updating template:', error);
    throw error;
  }
};

/**
 * Delete a template
 */
export const deleteTemplate = async (templateId) => {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase
      ?.from('campaign_templates')
      ?.delete()
      ?.eq('id', templateId)
      ?.eq('artist_id', user?.id);

    if (error) {
      throw new Error(error.message);
    }

    return true;
  } catch (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
};

/**
 * Increment template use count
 */
export const incrementTemplateUseCount = async (templateId) => {
  try {
    const { error } = await supabase?.rpc('increment_template_use_count', {
      template_id: templateId
    });

    if (error) {
      // If RPC doesn't exist, fallback to manual increment
      const { data: template } = await supabase
        ?.from('campaign_templates')
        ?.select('use_count')
        ?.eq('id', templateId)
        ?.single();

      if (template) {
        await supabase
          ?.from('campaign_templates')
          ?.update({ use_count: (template?.use_count || 0) + 1 })
          ?.eq('id', templateId);
      }
    }
  } catch (error) {
    console.error('Error incrementing template use count:', error);
    // Non-critical error, don't throw
  }
};

/**
 * Convert template data to campaign data format
 */
export const templateToCampaignData = (template) => {
  return {
    name: '',
    description: template?.description || '',
    musicTrackUrl: '',
    genre: template?.genre || '',
    objectives: template?.objectives || [],
    budget: template?.budget?.toString() || '',
    costPerEngagement: template?.cost_per_engagement?.toString() || '',
    paymentSchedule: template?.payment_schedule || 'milestone',
    contentTypes: template?.content_types || [],
    creativeGuidelines: template?.creative_guidelines || '',
    brandAssets: [],
    minFollowers: template?.min_followers?.toString() || '',
    maxFollowers: template?.max_followers?.toString() || '',
    minEngagement: template?.min_engagement?.toString() || '',
    contentSpecialization: template?.content_specialization || [],
    selectedInfluencers: [],
    hashtags: template?.hashtags || [],
    startDate: '',
    endDate: '',
    milestones: [],
    proposalTemplate: '',
    brandGuidelines: template?.creative_guidelines || '',
    hashtagRequirements: template?.hashtags?.join(', ') || '',
    goals: ''
  };
};

export default {
  fetchUserTemplates,
  fetchPublicTemplates,
  fetchTemplatesByGenre,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  incrementTemplateUseCount,
  templateToCampaignData
};